import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { useEffect, useRef, useState } from "react";

import styles from "../statistics.module.css";
import { formatDateWithDots } from "..";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
);

export function ThreeMonthsVolume({ threeMonthsVolume }) {
  const chartRef = useRef(null);
  const [data, setData] = useState({ labels: [], datasets: [] });

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    tension: 0.4,
    plugins: {
      tooltip: {
        enabled: false,
        external: function (context) {
          const chart = context.chart;
          const tooltipModel = context.tooltip;

          let existing = document.querySelector(".threeMonthsVolumeTooltip");
          if (existing) existing.remove();

          if (!tooltipModel.opacity || !tooltipModel.dataPoints.length) return;

          const tooltipEl = document.createElement("div");
          tooltipEl.className = "threeMonthsVolumeTooltip";
          tooltipEl.style.position = "absolute";
          tooltipEl.style.pointerEvents = "none";
          tooltipEl.style.padding = "8px 12px";
          tooltipEl.style.background = "#364048";
          tooltipEl.style.color = "#fff";
          tooltipEl.style.borderRadius = "8px";
          tooltipEl.style.fontSize = "14px";
          tooltipEl.style.fontWeight = "600";
          tooltipEl.style.whiteSpace = "nowrap";
          tooltipEl.style.zIndex = "1000";
          tooltipEl.style.transform = "translate(-50%, -100%)";

          const firstPoint = tooltipModel.dataPoints[0];
          const dateLabel = formatDateWithDots(firstPoint.raw.isoDate);

          // REVERSED ORDER OF CIRCLES
          tooltipModel.dataPoints
            .slice()
            .reverse()
            .forEach((dataPoint) => {
              const colorCircle = `
              <span style="
                display:inline-block;
                width:10px;
                height:10px;
                background:${dataPoint.dataset.borderColor};
                border-radius:50%;
                margin-right:6px;
              "></span>
            `;
              const value = Math.round(dataPoint.raw.y);
              const count = dataPoint.raw.count;
              const label = dataPoint.dataset.label;

              const row = document.createElement("div");
              row.style.marginBottom = "4px";
              row.innerHTML = `${colorCircle}${label}: ${count} / ${value}%`;
              tooltipEl.appendChild(row);
            });

          // Date row
          const dateRow = document.createElement("div");
          dateRow.style.fontSize = "12px";
          dateRow.style.marginTop = "4px";
          dateRow.innerText = dateLabel;
          tooltipEl.appendChild(dateRow);

          const canvasRect = chart.canvas.getBoundingClientRect();

          tooltipEl.style.top =
            canvasRect.top + window.scrollY + firstPoint.element.y - 230 + "px";
          tooltipEl.style.left =
            canvasRect.left + window.scrollX + firstPoint.element.x + "px";

          document.body.appendChild(tooltipEl);
        },
      },
    },
    interaction: {
      mode: "index",
      intersect: false,
    },
    elements: {
      point: {
        radius: 0,
        hoverRadius: 5,
        hoverBorderWidth: 2,
        backgroundColor: "#fff",
        borderWidth: 2,
      },
      line: { borderWidth: 8 },
    },
    scales: {
      x: { ticks: { autoSkip: true, maxTicksLimit: 3 } },
      y: {
        beginAtZero: true,
        ticks: { stepSize: 25, callback: (value) => `${value}%` },
      },
    },
  };

  useEffect(() => {
    if (!threeMonthsVolume?.length) return;

    const formattedData = threeMonthsVolume.map((item) => ({
      isoDate: item.date,
      monthLabel: new Date(item.date).toLocaleDateString("en-GB", {
        month: "short",
      }),
      below_1m: {
        percent: parseFloat(item.below_1m.percent),
        count: item.below_1m.count,
        isoDate: item.date,
      },

      between_1m_10m: {
        percent: parseFloat(item.between_1m_10m.percent),
        count: item.between_1m_10m.count,
        isoDate: item.date,
      },

      above_10m: {
        percent: parseFloat(item.above_10m.percent),
        count: item.above_10m.count,
        isoDate: item.date,
      },
    }));

    const labels = formattedData.map((d) => d.monthLabel);

    const chart = chartRef.current;
    if (!chart) return;

    const { ctx, chartArea } = chart;
    if (!chartArea) return;
    const { top, bottom } = chartArea;

    const createGradient = (colorStart, colorStop, midStop) => {
      const gradient = ctx.createLinearGradient(0, top, 0, bottom);
      gradient.addColorStop(0, colorStart);
      if (midStop) gradient.addColorStop(midStop[0], midStop[1]);
      gradient.addColorStop(1, colorStop);
      return gradient;
    };
    const datasets = [
      {
        label: "Below $1M",
        data: formattedData.map((d) => ({
          x: d.monthLabel,
          y: d.below_1m.percent,
          isoDate: d.isoDate,
          count: d.below_1m.count,
        })),
        borderColor: "rgba(52, 179, 241, 1)",
        borderWidth: 2,
        fill: true,
        backgroundColor: createGradient("#34b3f1ff", "rgba(255,255,255,0)", [
          0.9,
          "rgba(52, 179, 241, 0)",
        ]),
      },
      {
        label: "$1M–$10M",
        data: formattedData.map((d) => ({
          x: d.monthLabel,
          y: d.between_1m_10m.percent,
          isoDate: d.isoDate,
          count: d.between_1m_10m.count,
        })),
        borderColor: "rgba(65, 143, 222, 1)",
        borderWidth: 2,
        fill: true,
        backgroundColor: createGradient(
          "rgba(65, 143, 222, 1)",
          "rgba(255,255,255,0)",
          [0.9, "rgba(65, 143, 222, 0)"],
        ),
      },
      {
        label: "Above $10M",
        data: formattedData.map((d) => ({
          x: d.monthLabel,
          y: d.above_10m.percent,
          isoDate: d.isoDate,
          count: d.above_10m.count,
        })),
        borderColor: "rgba(35, 97, 146, 1)",
        borderWidth: 2,
        fill: true,
        backgroundColor: createGradient(
          "rgba(35, 97, 146, 1)",
          "rgba(255,255,255,0)",
          [0.9, "rgba(35, 97, 146, 0)"],
        ),
      },
    ];

    setData({ labels, datasets });
  }, [threeMonthsVolume]);

  return (
    <div className={styles.statisticsContent} style={{ width: "45%" }}>
      <h2 className={styles.statisticsTitle}>Average 3 Months Volume</h2>
      <div className={styles.statisticsColoredBar}>
        <div
          className={styles.statisticTableWrapper}
          style={{ marginTop: "5px", width: "90%", marginInline: "auto" }}
        >
          <div
            className={styles.statisticTableOneColumn}
            style={{ width: "100%", maxWidth: "900px", height: "330px" }}
          >
            <Line
              ref={chartRef}
              data={data}
              options={options}
              width={1600}
              height={330}
            />
          </div>
        </div>
      </div>

      <div className={styles.divider}></div>

      <div className={styles.legendWrapper}>
        <div className={styles.legend}>Legend</div>
        <div className={styles.legend}>
          <span
            className={styles.redColoresSquare}
            style={{ background: "#34B3F1" }}
          ></span>
          Below $1M
        </div>
        <div className={styles.legend}>
          <span
            className={styles.yellowColoresSquare}
            style={{ background: "#418FDE" }}
          ></span>
          $1M–$10M
        </div>
        <div className={styles.legend}>
          <span
            className={styles.greenColoresSquare}
            style={{ background: "#236192" }}
          ></span>
          Above $10M
        </div>
      </div>
    </div>
  );
}